import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useViewport from "./useViewport";
import { uuid } from "@disco/disco_core";
import { VIEWPORT_MANAGER_REDUCER_TYPE } from "../components/managers/ViewportManager";

const useElementInView = ({
	elementRef = null,
	topBuffer = 0,
	active = true,
} = {}) => {
	const [isTopInView, setIsTopInView] = useState(null);
	const [isBottomInView, setIsBottomInView] = useState(null);

	const callbackRef = useRef(null);

	const { manageCallbacks, requestParentStats } = useViewport();
	const mostRecentData = useRef(null);

	const isFullyInView = useMemo(
		() =>
			isTopInView === null || isBottomInView === null
				? null
				: isBottomInView && isTopInView,
		[isBottomInView, isTopInView]
	);
	const isPartiallyInView = useMemo(
		() => isTopInView || isBottomInView,
		[isTopInView, isBottomInView]
	);

	const handleParentStatsUpdate = useCallback(
		(data) => {
			mostRecentData.current = data;
			const { widget } = data || {};

			if (!elementRef?.current || !widget) {
				setIsTopInView(null);
				setIsBottomInView(null);
				return;
			}
			const { top, height } = elementRef.current.getBoundingClientRect();
			const bottom = top + height;
			const { visibleFrameStart, visibleFrameEnd } = widget;
			const isParamInView = (param) =>
				!!(param > visibleFrameStart && param < visibleFrameEnd);
			setIsTopInView(
				isParamInView(top) || isParamInView(top + topBuffer)
			);
			setIsBottomInView(isParamInView(bottom));
		},
		[elementRef, topBuffer]
	);

	const attachCallback = useCallback(() => {
		callbackRef.current = uuid();
		manageCallbacks({
			type: VIEWPORT_MANAGER_REDUCER_TYPE.ADD_CALLBACK,
			idx: callbackRef.current,
			callback: handleParentStatsUpdate,
		});
	}, [manageCallbacks, handleParentStatsUpdate]);

	const detachCallback = useCallback(() => {
		if (!callbackRef.current) return;
		manageCallbacks({
			type: VIEWPORT_MANAGER_REDUCER_TYPE.REMOVE_CALLBACK,
			idx: callbackRef.current,
			callback: null,
		});
		callbackRef.current = null;
	}, [manageCallbacks]);

	const triggerRecompute = useCallback(
		() => handleParentStatsUpdate(mostRecentData.current),
		[handleParentStatsUpdate]
	);
	useEffect(() => {
		attachCallback();
		return () => detachCallback();
	}, [attachCallback, detachCallback]);

	useEffect(() => {
		if (!active) return;
		requestParentStats();
	}, [active, requestParentStats]);

	return {
		triggerRecompute,
		requestParentStats,
		isTopInView,
		isBottomInView,
		isFullyInView,
		isPartiallyInView,
	};
};
export default useElementInView;
